/**
 * Alshallal Pastry Company HR System - Forgot Password Page
 * Request password reset email
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPasswordPage = () => {
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [error, setError] = useState('');

  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/password-reset/request`, {
        identifier
      });
      
      setEmailConfigured(response.data.email_configured);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail?.error || t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="forgot-password-success">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            {emailConfigured ? (
              <>
                <div className="p-4 bg-green-100 rounded-full w-fit mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">
                  {isRTL ? 'تم إرسال الرابط' : 'Reset Link Sent'}
                </h2>
                <p className="text-slate-600 mb-6">
                  {isRTL 
                    ? 'إذا كان الحساب موجوداً، سيتم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.'
                    : 'If an account exists with this identifier, a password reset link will be sent to your email.'
                  }
                </p>
              </>
            ) : (
              <>
                <div className="p-4 bg-amber-100 rounded-full w-fit mx-auto mb-6">
                  <AlertCircle className="h-10 w-10 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">
                  {isRTL ? 'البريد الإلكتروني غير مُهيأ' : 'Email Not Configured'}
                </h2>
                <p className="text-slate-600 mb-6">
                  {isRTL 
                    ? 'نظام البريد الإلكتروني غير مُهيأ حالياً. يرجى التواصل مع مسؤول النظام.'
                    : 'The email system is not currently configured. Please contact your system administrator.'
                  }
                </p>
              </>
            )}
            
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <BackArrow className="h-4 w-4 me-2" />
                {isRTL ? 'العودة لتسجيل الدخول' : 'Back to Login'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {/* Language toggle */}
        <div className="flex justify-end mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
          >
            {language === 'en' ? 'العربية' : 'English'}
          </Button>
        </div>

        <Card>
          <CardHeader className="text-center">
            <div className="p-3 bg-slate-100 rounded-full w-fit mx-auto mb-4">
              <Mail className="h-8 w-8 text-slate-600" />
            </div>
            <CardTitle className="text-2xl">
              {isRTL ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
            </CardTitle>
            <CardDescription>
              {isRTL 
                ? 'أدخل بريدك الإلكتروني أو رقم الهوية لإعادة تعيين كلمة المرور'
                : 'Enter your email or ID to reset your password'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="identifier">
                  {isRTL ? 'البريد الإلكتروني / الهوية / الإقامة' : 'Email / National ID / Iqama'}
                </Label>
                <Input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t('identifier_placeholder')}
                  required
                  disabled={loading}
                  className="h-11"
                  data-testid="reset-identifier"
                  dir="ltr"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading || !identifier.trim()}
                data-testid="reset-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  isRTL ? 'إرسال رابط إعادة التعيين' : 'Send Reset Link'
                )}
              </Button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                >
                  <BackArrow className="h-4 w-4" />
                  {isRTL ? 'العودة لتسجيل الدخول' : 'Back to Login'}
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
